import React from "react";
import PropTypes from "prop-types";
import { FormatCurrency, FormatPoints } from "../../../lib/FormatCurrency";
import { useApp } from "../../../contexts/AppContext";
import {
  Container,
  Header,
  ProductName,
  StatusTracker,
  StatusStep,
  StatusIcon,
  StatusLine,
  StatusLabel,
  StatusDate,
  ProductDetails,
  ProductRow,
  ProductDescription,
  ProductPrice,
  Divider,
  TotalRow,
  Label,
  Value,
  NoTrackingContainer,
  NoTrackingTitle,
  NoTrackingMessage,
} from "./styles";

function ProductCard({ product, hideTracking }) {
  const productData = product || {};
  const { whitelabel } = useApp();

  const calculateTotalPoints = () => {
    if (!Array.isArray(productData.products)) return 0;

    return productData.products.reduce((total, prod) => {
      const points = Number(prod.points) || 0;
      const quantity = Number(prod.quantity) || 1;
      return total + points * quantity;
    }, 0);
  };

  const calculateTotalReais = () => {
    if (!Array.isArray(productData.products)) return 0;

    return productData.products.reduce((total, prod) => {
      const reais = Number(prod.price) || 0;
      const quantity = Number(prod.quantity) || 1;
      return total + reais * quantity;
    }, 0);
  };

  const getDiscountInPoints = () => {
    const discount = parseFloat(productData.discount) || 0;
    if (discount <= 0) return 0;

    const configData = whitelabel?.config || {};
    const channel = process.env.REACT_APP_DEFAULT_CHANNEL;
    const fatorFreteKey = `fator_${channel}_frete`;
    const fatorFrete = configData[fatorFreteKey] / 10000 || 0;

    if (fatorFrete <= 0) return 0;

    // Conversão consistente com SliderCheckoutCoupon: factor = 1/fatorFrete
    // pontos = dinheiro * factor = dinheiro / fatorFrete
    const factor = 1 / fatorFrete;
    return Math.round(discount * factor);
  };

  const totalProductPoints = calculateTotalPoints();
  const shipping = productData.shippingPoints || 0;
  const discountInPoints = getDiscountInPoints();
  const total = Math.max(0, totalProductPoints + shipping - discountInPoints);
  const totalProductReais = calculateTotalReais();
  const shippingReais = productData.shipping || 0;
  const totalReais = Math.max(0, totalProductReais + shippingReais - productData.discount);

  return (
    <Container>
      <Header>
        <ProductName>{productData.brand || "-"}</ProductName>
      </Header>

      {!hideTracking && (
        productData.hasTracking ? (
          <StatusTracker>
            {Array.isArray(productData.status) &&
              productData.status.map((step, index) => (
                <StatusStep
                  key={index}
                  isLast={index === productData.status.length - 1}
                >
                  <StatusIcon completed={step.completed}>
                    {step.completed && (
                      <svg
                        width="12"
                        height="10"
                        viewBox="0 0 12 10"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1 5L4.5 8.5L11 1.5"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </StatusIcon>
                  {index < productData.status.length - 1 && (
                    <StatusLine
                      completed={
                        step.completed && productData.status[index + 1]?.completed
                      }
                    />
                  )}
                  <div>
                    <StatusLabel>{step.label || "-"}</StatusLabel>
                    {step.date && <StatusDate>{step.date || "-"}</StatusDate>}
                  </div>
                </StatusStep>
              ))}
          </StatusTracker>
        ) : (
          <NoTrackingContainer>
            <NoTrackingTitle>Acompanhamento de entrega</NoTrackingTitle>
            <NoTrackingMessage>
              Essa entrega não possui acompanhamento em tempo real. Caso não
              receba até{" "}
              <strong>{productData.deliveryDate || "a data prometida"}</strong>,
              entre em contato com o atendimento ao cliente.
            </NoTrackingMessage>
          </NoTrackingContainer>
        )
      )}

      <ProductDetails>
        <ProductRow style={{ display: "flex", gap: "25%" }}>
          <ProductDescription>{productData.name || "-"}</ProductDescription>
          <ProductPrice>
            {whitelabel?.config?.payment_mode === "money"
              ? totalProductReais
                ? FormatCurrency(totalProductReais)
                : "R$ 0,00"
              : `${totalProductPoints ? FormatPoints(totalProductPoints) : "0,00"} pts`}
          </ProductPrice>
        </ProductRow>

        {!!productData.discount && (
          <ProductRow>
            <Label>Cupom aplicado:</Label>
            <Value>
              {whitelabel?.config?.payment_mode === "money"
                ? FormatCurrency(productData.discount)
                : `${discountInPoints ? FormatPoints(discountInPoints) : "0,00"} pts`}
            </Value>
          </ProductRow>
        )}

        <ProductRow>
          <Label>Frete:</Label>
          <Value>
            {whitelabel?.config?.payment_mode === "money"
              ? shippingReais
                ? FormatCurrency(shippingReais)
                : "R$ 0,00"
              : `${shipping ? FormatPoints(shipping) : "0,00"} pts`}
          </Value>
        </ProductRow>

        <ProductRow>
          <Label isBold>Subtotal:</Label>
          <Value isBold>
            {whitelabel?.config?.payment_mode === "money"
              ? FormatCurrency(totalReais)
              : `${FormatPoints(total)} pts`}
          </Value>
        </ProductRow>

        <Divider />

        <TotalRow>
          <Label isBold>Previsão de entrega</Label>
          <Value isBold>{productData.deliveryDate || "-"}</Value>
        </TotalRow>
      </ProductDetails>
    </Container>
  );
}

ProductCard.propTypes = {
  hideTracking: PropTypes.bool,
  product: PropTypes.shape({
    brand: PropTypes.string,
    name: PropTypes.string,
    price: PropTypes.number,
    shipping: PropTypes.number,
    deliveryDate: PropTypes.string,
    hasTracking: PropTypes.bool,
    status: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        date: PropTypes.string,
        completed: PropTypes.bool,
      }),
    ),
  }),
};

ProductCard.defaultProps = {
  product: null,
};

export default ProductCard;
